package com.advaita.cloud.drive.ui.store

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import com.advaita.cloud.drive.shared.PickerItemDto
import com.advaita.cloud.drive.shared.PickerResponse
import com.advaita.cloud.drive.shared.SourceStatus
import com.advaita.cloud.drive.ui.api.RedirectingException
import com.advaita.cloud.drive.ui.api.ShareApiException
import com.advaita.cloud.drive.ui.components.ShareCap
import com.advaita.cloud.drive.ui.components.capForError
import com.advaita.cloud.drive.ui.components.capSentence
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch

/** Review ruling 2: the picker waits until the user stops typing before it searches. */
const val PEOPLE_DEBOUNCE_MS = 300L

/** Ruling S3: no person is listed under 2 characters. */
const val PEOPLE_MIN_CHARS = 2

/** Ruling S4: at most 8 recents. DataHub already caps them, and the UI does not trust that. */
const val PEOPLE_MAX_RECENTS = 8

/** The kind icon of a picker row (S5: one merged list, an icon for each kind). */
enum class PickerKind { PERSON, TEAM }

fun pickerKind(item: PickerItemDto): PickerKind = if (item.kind == "TEAM") PickerKind.TEAM else PickerKind.PERSON

/** The label of a picker row. A picker row always has a name, but a null one must not render "null". */
fun pickerLabel(item: PickerItemDto): String = item.name ?: item.email ?: if (item.kind == "TEAM") "Team" else "This person"

/** The second line of a picker row: a person's email, a team's size. */
fun pickerMeta(item: PickerItemDto): String? = when (pickerKind(item)) {
    PickerKind.PERSON -> item.email
    PickerKind.TEAM -> item.memberCount?.let { if (it == 1) "1 member" else "$it members" }
}

/**
 * Why a row cannot be picked, or null when it can (S4, S8 row 6). Only a person with no vault
 * directory row is blocked: `signedIn` null means the vault could not say, and the vault still
 * judges the grant itself.
 */
fun pickerBlockedSentence(item: PickerItemDto): String? =
    if (!item.selectable || item.signedIn == false) capSentence(ShareCap.NOT_SIGNED_IN, person = pickerLabel(item)) else null

/** The one sentence for a throttled search (429), with the wait when DataHub gave one. */
fun searchThrottledSentence(retryAfterSeconds: Int?): String =
    if (retryAfterSeconds != null && retryAfterSeconds > 0) {
        "Too many searches in a short time. Try again in $retryAfterSeconds s."
    } else {
        "Too many searches in a short time. Wait a moment and try again."
    }

/**
 * The line under a search that matched nobody. The search matches one name or the email by
 * prefix (Tim's search-scope ruling), so "lena smith" finds nobody; with a space in the query,
 * the line says so.
 */
fun noMatchLine(q: String): String {
    val base = "No people or teams match \"$q\"."
    return if (q.trim().contains(' ')) "$base Search one name at a time." else base
}

/** The notes under a picker list that is short because one source did not answer (S5). */
fun pickerNotes(peopleStatus: String, teamsStatus: String): List<String> = buildList {
    when (peopleStatus) {
        SourceStatus.REFUSED -> add("People search is not available for your account.")
        SourceStatus.UNAVAILABLE -> add("People search is temporarily unavailable.")
    }
    when (teamsStatus) {
        SourceStatus.REFUSED -> add("Teams are not available for your account.")
        SourceStatus.UNAVAILABLE -> add("Teams are temporarily unavailable.")
    }
}

/**
 * ADV-1330 F1: the state of the share dialog's people picker. DOM-free on purpose, like
 * [SearchStore]: jsTest runs under Node, so the two reads are injected and the scope is
 * injectable for virtual-time tests.
 *
 * - An empty box shows the caller's recents (S4), read once on [open]. The browser stores none
 *   of them: the list lives in this object, and [close] drops it.
 * - One character shows nobody and calls nothing (S3).
 * - Two or more characters search once the user stops typing for [PEOPLE_DEBOUNCE_MS].
 * - People and teams arrive in one list (S5). A source that did not answer adds a note.
 * - A person with `signedIn: false` is listed and greyed, and [pick] refuses him (S4).
 * - A `429` holds every search for the `Retry-After` it gave, then runs the latest query.
 * - The keyboard moves [activeIndex] over the rows ([moveActive]) and picks it ([pickActive]).
 */
class SharePickerStore(
    private val search: suspend (q: String) -> PickerResponse,
    private val recent: suspend () -> PickerResponse,
    private val scope: CoroutineScope = MainScope(),
) {
    var query: String by mutableStateOf("")
        private set
    var items: List<PickerItemDto> by mutableStateOf(emptyList())
        private set
    var showingRecents: Boolean by mutableStateOf(false)
        private set
    var isSearching: Boolean by mutableStateOf(false)
        private set
    var picked: PickerItemDto? by mutableStateOf(null)
        private set

    /** The keyboard's row, or -1 for none. The input names it with `aria-activedescendant`. */
    var activeIndex: Int by mutableStateOf(-1)
        private set

    /** The trimmed query whose answer is on screen. Drives the "no matches" line. */
    var completedQuery: String? by mutableStateOf(null)
        private set

    /** The notes under the list: a source down, the throttle, or a failed read. */
    var notes: List<String> by mutableStateOf(emptyList())
        private set

    /** True while a `429` holds the searches. */
    var throttled: Boolean by mutableStateOf(false)
        private set

    private var recents: List<PickerItemDto> = emptyList()
    private var hidden: Set<String> = emptySet()
    private var recentsNotes: List<String> = emptyList()
    private var debounceJob: Job? = null
    private var recentJob: Job? = null
    private var holdJob: Job? = null
    private var requestSeq = 0
    private var openSeq = 0

    /** Reads the recents and shows them on the empty box. */
    fun open() {
        close()
        showingRecents = true
        val opened = openSeq
        recentJob = scope.launch {
            val (list, listNotes) = try {
                val r = recent()
                r.items to pickerNotes(r.peopleStatus, r.teamsStatus)
            } catch (e: CancellationException) {
                throw e
            } catch (e: RedirectingException) {
                throw e
            } catch (t: Throwable) {
                val cap = (t as? ShareApiException)?.let { capForError(it, notFoundIsGate = true) }
                // A cap the whole session shares (the gate, row 5) says so; an outage of the
                // ledger alone keeps search working and says only that the recents are missing.
                val note = if (cap != null && cap != ShareCap.OUTAGE) capSentence(cap) else "Recent recipients are temporarily unavailable."
                emptyList<PickerItemDto>() to listOf(note)
            }
            if (opened != openSeq) return@launch
            recents = list
            recentsNotes = listNotes
            if (query.trim().isEmpty()) {
                setItems(shownRecents())
                notes = listNotes
            }
        }
    }

    /**
     * Tim's nit (2026-09-30): the people and teams that "Who has access" lists, as `KIND:id`
     * keys. Recent leaves them out, and the cap of 8 counts after the filter. [close] keeps the
     * set, because the dialog re-reads the access list after each write and sets it again.
     * Example: Milan already has Editor, so Recent does not offer him.
     */
    fun hideFromRecents(keys: Set<String>) {
        hidden = keys
        if (showingRecents && query.trim().isEmpty()) setItems(shownRecents())
    }

    /** Adds one `KIND:id` key to the hidden set. The dialog uses it for the person it just shared with. */
    fun alsoHideFromRecents(key: String) = hideFromRecents(hidden + key)

    private fun shownRecents(): List<PickerItemDto> =
        recents.filter { "${it.kind}:${it.id}" !in hidden }.take(PEOPLE_MAX_RECENTS)

    fun close() {
        debounceJob?.cancel()
        recentJob?.cancel()
        holdJob?.cancel()
        requestSeq++
        openSeq++
        query = ""
        setItems(emptyList())
        notes = emptyList()
        recents = emptyList()
        recentsNotes = emptyList()
        showingRecents = false
        isSearching = false
        throttled = false
        picked = null
        completedQuery = null
    }

    fun onQueryChanged(text: String) {
        query = text
        debounceJob?.cancel()
        val trimmed = text.trim()
        val seq = ++requestSeq
        if (trimmed.isEmpty()) {
            showingRecents = true
            setItems(shownRecents())
            notes = if (throttled) notes else recentsNotes
            isSearching = false
            completedQuery = null
            return
        }
        showingRecents = false
        if (trimmed.length < PEOPLE_MIN_CHARS) {
            setItems(emptyList()); if (!throttled) notes = emptyList(); isSearching = false; completedQuery = null
            return
        }
        // Held by a 429: the hold runs the latest query when it ends.
        if (throttled) return
        debounceJob = scope.launch {
            delay(PEOPLE_DEBOUNCE_MS)
            runSearch(trimmed, seq)
        }
    }

    private suspend fun runSearch(trimmed: String, seq: Int) {
        isSearching = true
        try {
            val r = search(trimmed)
            if (seq != requestSeq) return
            setItems(r.items)
            notes = pickerNotes(r.peopleStatus, r.teamsStatus)
            completedQuery = trimmed
        } catch (e: CancellationException) {
            throw e
        } catch (e: RedirectingException) {
            throw e
        } catch (t: Throwable) {
            if (seq != requestSeq) return
            setItems(emptyList())
            completedQuery = null
            if (t is ShareApiException && t.status == 429) hold(t.retryAfterSeconds)
            else notes = listOf(searchFailureNote(t))
        } finally {
            if (seq == requestSeq) isSearching = false
        }
    }

    /** Holds every search for [seconds] (or a short default), then runs the latest query. */
    private fun hold(seconds: Int?) {
        throttled = true
        notes = listOf(searchThrottledSentence(seconds))
        holdJob?.cancel()
        val opened = openSeq
        holdJob = scope.launch {
            delay(((seconds?.takeIf { it > 0 } ?: DEFAULT_HOLD_S) * 1000).toLong())
            if (opened != openSeq) return@launch
            throttled = false
            notes = emptyList()
            onQueryChanged(query)
        }
    }

    /** Picks [item] when it can be picked, and clears the box. False for Sam (S4). */
    fun pick(item: PickerItemDto): Boolean {
        if (pickerBlockedSentence(item) != null) return false
        picked = item
        onQueryChanged("")
        return true
    }

    /** Moves the keyboard's row by [delta], clamped to the list. */
    fun moveActive(delta: Int) {
        if (items.isEmpty()) { activeIndex = -1; return }
        activeIndex = (if (activeIndex < 0 && delta > 0) 0 else activeIndex + delta).coerceIn(0, items.lastIndex)
    }

    /** Picks the keyboard's row. False when there is none, or it cannot be picked. */
    fun pickActive(): Boolean = items.getOrNull(activeIndex)?.let(::pick) ?: false

    fun clearPick() {
        picked = null
    }

    private fun setItems(list: List<PickerItemDto>) {
        items = list
        activeIndex = -1
    }

    private fun searchFailureNote(t: Throwable): String {
        val cap = (t as? ShareApiException)?.let { capForError(it, notFoundIsGate = true) }
        return cap?.let { capSentence(it) } ?: "Search failed. Try again."
    }

    private companion object {
        const val DEFAULT_HOLD_S = 10
    }
}
